import {
  BaseIcon,
  Button,
  Modal,
  SelectMenu,
  Spinner,
  TextArea,
  TextField,
  Typography,
} from '@scale/scaleui';
import { TaskType } from '@scale/shared-types';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useChatBulkAuditFilterComponents } from 'react-scale/components/ExpertManagement/ExpertManagementPage/BulkAudit/useChatBulkAuditFilterComponents';
import { useChatBulkAuditFiltering } from 'react-scale/components/ExpertManagement/ExpertManagementPage/BulkAudit/useChatBulkAuditFiltering';
import { useBeginAudit } from 'react-scale/components/ExpertManagement/hooks/audit/useBeginAudit';
import { ExpertInfo } from './UsersChatBulkAuditModal';

const TASKS_LIMIT = 100;
const MULTIPLE_EXPERTS_TASK_LIMIT = 20;

const SelectionOptions = {
  Filters: 'Filters',
  ObjectIDs: 'ObjectIDs',
};

const CustomCardSelection: React.FC<{
  title: string;
  description: string;
  isSelected: boolean;
  isDisabled: boolean;
  onSelection: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ title, description, isSelected, isDisabled, onSelection }) => {
  return (
    <Button
      onClick={onSelection}
      size="lg"
      variant={isSelected ? 'outline' : 'white'}
      disabled={isDisabled}
    >
      <div className="flex flex-row items-center h-14">
        <div className="text-left">
          <Typography.Subtitle3 className="text-wrap">{title}</Typography.Subtitle3>
          <Typography.Caption className="text-wrap w-72">{description}</Typography.Caption>
        </div>
        <BaseIcon
          className="text-inherit"
          edge="start"
          icon={[isSelected ? 'fas' : 'far', isSelected ? 'check-circle' : 'circle']}
          variant="neutral"
          size="lg"
        />
      </div>
    </Button>
  );
};

/* eslint-disable complexity */
export const UserChatBulkAuditModal: React.FC<{
  onClose: () => void;
  tasks: any[];
  isOpen: boolean;
  experts?: ExpertInfo[];
  loadingTasks?: boolean;
}> = ({ onClose, tasks, isOpen, experts = [], loadingTasks = false }) => {
  const [objectIds, setObjectIds] = useState('');

  const parsedObjectIds = useMemo(
    () =>
      objectIds
        .split(/\s+|,/)
        .map((id: string) => id.trim())
        .filter((id: string) => id),
    [objectIds],
  );

  const projectIdOptions = useMemo(
    () =>
      _.sortBy(
        _.uniqBy(tasks, t => t.project._id).map(task => ({
          id: task.project._id,
          name: task.project.name,
        })),
        'name',
      ),
    [tasks],
  );

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const [projectTasks, projectReviewLevels] = useMemo(() => {
    const projectTasks = tasks.filter(task => task.project._id === selectedProjectId);
    const projectReviewLevels = _.uniq(projectTasks.map(task => task.attemptedAtReviewLevel));

    return [projectTasks, projectReviewLevels];
  }, [tasks, selectedProjectId]);

  const [selectedSource, setSelectedSource] = useState('');

  const [sampleNumber, setSampleNumber] = useState<number | undefined>(undefined);

  const {
    bodyFilters,
    renderCustomFiltersComponent,
    renderQuickFiltersComponent,
    selectedQuickFilter,
  } = useChatBulkAuditFilterComponents(projectReviewLevels);

  const { filteredTasks } = useChatBulkAuditFiltering(projectTasks, bodyFilters);

  const isCreateEnabled: boolean = !!(
    (selectedSource === SelectionOptions.ObjectIDs && objectIds.trim() !== '') ||
    (selectedSource === SelectionOptions.Filters &&
      sampleNumber &&
      sampleNumber > 0 &&
      sampleNumber <= (experts.length > 1 ? MULTIPLE_EXPERTS_TASK_LIMIT : TASKS_LIMIT) &&
      filteredTasks.length > 0)
  );

  const { beginAudit, beginAuditLoading } = useBeginAudit();

  const onCreateChatBulkAudit = useCallback(async () => {
    if (selectedSource === SelectionOptions.ObjectIDs) {
      await beginAudit({
        projectId: selectedProjectId,
        taskIds: parsedObjectIds,
        chatBulkAudit: filteredTasks[0].taskType === TaskType.Chat,
      });
    } else {
      const sortedTasks = _.sortBy(filteredTasks, 'attemptedAt').reverse();
      let selectedTaskIds = sortedTasks.slice(0, sampleNumber).map(task => task._id);
      if (experts.length > 1) {
        const groupedTasks = _.groupBy(sortedTasks, 'attemptedBy');
        selectedTaskIds = [];
        for (const tasks of Object.values(groupedTasks)) {
          selectedTaskIds = selectedTaskIds.concat(tasks.slice(0, sampleNumber).map(t => t._id));
        }
      }
      await beginAudit({
        projectId: selectedProjectId,
        taskIds: selectedTaskIds,
        chatBulkAudit: filteredTasks[0].taskType === TaskType.Chat,
      });
    }

    onClose();
  }, [
    beginAudit,
    filteredTasks,
    onClose,
    parsedObjectIds,
    sampleNumber,
    selectedProjectId,
    selectedSource,
    experts.length,
  ]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      theme="scale-theme"
      title="Create a new Bulk Audit"
      className="scaleui scale-tailwind"
    >
      <div className="h-full flex flex-col space-y-6">
        {loadingTasks && (
          <div className="flex gap-2 items-center">
            Loading tasks...
            <Spinner size="sm" />
          </div>
        )}
        {!!experts?.length && (
          <div className="flex flex-col gap-2">
            <Typography.Subtitle3>Selected Experts:</Typography.Subtitle3>
            <div className="flex flex-col gap-1 flex-wrap mb-2 mr-6 max-h-[200px]">
              {experts.map((expert: ExpertInfo) => (
                <Typography.Paragraph2 key={`selected-experts-${expert.name}`}>
                  • {expert.name} ({filteredTasks.filter(t => t.attemptedBy === expert.id).length})
                </Typography.Paragraph2>
              ))}
            </div>
          </div>
        )}
        <div className="flex-1 space-y-4">
          <Typography.Subtitle3>Select a project to audit:</Typography.Subtitle3>
          <div>
            <SelectMenu
              options={projectIdOptions}
              selectedOptionId={selectedProjectId}
              setSelected={setSelectedProjectId}
            />
          </div>
        </div>
        <div className="flex-1 space-y-4">
          <Typography.Subtitle3>Select a source for your audit</Typography.Subtitle3>
          <div className="flex space-x-4 items-center">
            <CustomCardSelection
              title={'Find Tasks'}
              description={'Select custom filters for tasks to sample from'}
              isSelected={selectedSource === SelectionOptions.Filters}
              isDisabled={!selectedProjectId}
              onSelection={() => setSelectedSource(SelectionOptions.Filters)}
            />
            <CustomCardSelection
              title={'Object IDs'}
              description={'Paste object IDs for tasks or task attempts.'}
              isSelected={selectedSource === SelectionOptions.ObjectIDs}
              isDisabled={!selectedProjectId}
              onSelection={() => setSelectedSource(SelectionOptions.ObjectIDs)}
            />
          </div>
        </div>
        {selectedSource === SelectionOptions.Filters && (
          <>
            <div className="flex-1 space-y-4">
              {renderQuickFiltersComponent()}
              {selectedQuickFilter === 'Custom Filter' && renderCustomFiltersComponent()}
            </div>
            <div className="flex-1 space-y-4">
              <Typography.Subtitle3>
                Number of most recent tasks to sample {experts.length > 1 ? 'from each expert' : ''}{' '}
                (max {experts?.length > 1 ? MULTIPLE_EXPERTS_TASK_LIMIT : TASKS_LIMIT})
              </Typography.Subtitle3>
              <TextField
                className="w-32"
                type="number"
                value={sampleNumber}
                onChange={e => {
                  // only update state if input is empty (to allow clearing the input) or contains only digits
                  if (e.target.value === '' || e.target.value === undefined) {
                    setSampleNumber(undefined);
                  } else {
                    setSampleNumber(Number(e.target.value));
                  }
                }}
                placeholder="Enter a number"
              />
            </div>
          </>
        )}
        {selectedSource === SelectionOptions.ObjectIDs && (
          <div className="flex-1 space-y-4">
            <Typography.Subtitle3>Enter Object IDs</Typography.Subtitle3>
            <TextArea
              className="w-96"
              rows={8}
              value={objectIds}
              onChange={e => {
                setObjectIds(e.target.value);
              }}
            />
          </div>
        )}
        <Typography.Subtitle3>
          {filteredTasks.length} tasks found with attempts
        </Typography.Subtitle3>
        <div className="h-[1px] bg-neutral-200 -mx-4" />
        <div className="flex justify-end">
          <Button onClick={onClose} variant="neutral">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await onCreateChatBulkAudit();
            }}
            className="ml-2"
            variant="primary"
            disabled={!isCreateEnabled || beginAuditLoading}
          >
            Create Audit
          </Button>
        </div>
      </div>
    </Modal>
  );
};
